import { CheckoutItem, CheckoutItemHelper } from './checkoutItem';

import { Payment } from './payment';

import { User, UserHelper } from './user';
import { Address } from './address';
import { Cart, CartHelper } from './cart';

/**
 * Status of checkout
 *
 * @export
 * @enum {number}
 */
export enum CheckoutStatus {
    Validated,
    Validating
}

export interface Checkout {
    items: CheckoutItem[];
    status: CheckoutStatus;
    cart: Cart;
    payments: Payment[];
    selectedPayment: string;
    annotations: string;
    agency: boolean;
    user: User;
    idPromo: string;
    eid: string;

    address: Address;
    addressIndex: number;

    // Totals
    importeArticulos: number;
    importeBruto: number;
    importeBrutoIncluidoDescuento: number;
    importeDescuento: number;
    importeIva: number;
    importeNfu: number;
    importePortes: number;
    importePromos: number;
    importeTotal: number;
    importeSubtotal: number;
    puntos: number;
    textoImpuesto: string;

    // Replacement Order
    activeReplacementOrder: boolean;
    replacementOrderName: string;

    matricula: string;
    numAutorizacion: string;
}

export class CheckoutHelper {
    static create(): Checkout {
        return {
            items: [],
            status: CheckoutStatus.Validated,
            cart: null,
            payments: [],
            selectedPayment: null,
            annotations: '',
            agency: false,
            user: null,
            idPromo: '',
            eid: '',

            address: null,
            addressIndex: 0,

            // Totals
            importeArticulos: 0,
            importeBruto: 0,
            importeBrutoIncluidoDescuento: 0,
            importeDescuento: 0,
            importeIva: 0,
            importeNfu: 0,
            importePortes: 0,
            importePromos: 0,
            importeTotal: 0,
            importeSubtotal: 0,
            puntos: 0,
            textoImpuesto: '',

            activeReplacementOrder: false,
            replacementOrderName: '',

            matricula: '',
            numAutorizacion: ''
        };
    }

    /**
     * Set user reference
     *
     * @memberOf Checkout
     */
    static setUser(checkout: Checkout, user: User) {
        checkout.user = user;
        if (user != null) {
            checkout.address = UserHelper.getSelectedAddress(user);
        }
    }

    /**
     * Create a Checkout from GenericObject
     *
     * @static
     * @param {Object} checkoutObj
     * @returns {Checkout}
     */
    static fromObject(checkoutObj: Object): Checkout {
        const obj = CheckoutHelper.create();
        Object.assign(checkoutObj, obj);
        return obj;
    }

    /**
     * Returns index of selected address
     *
     * @readonly
     * @type {number}
     * @memberOf Checkout
     */
    static getAddressIndex(checkout: Checkout): number {
        return checkout.addressIndex;
    }

    /**
     * Set address by reference
     *
     *
     * @memberOf Checkout
     */
    static setAddress(checkout: Checkout, address: Address) {
        checkout.address = address;
    }

    /**
     * Obtain current selected address
     *
     * @readonly
     * @type {Address}
     * @memberOf Checkout
     */
    static getAddress(checkout: Checkout): Address {
        return checkout.address;
    }

    /**
     * Set cart reference and expand checkout items
     * with shippings.
     *
     * @param {Cart} cart
     *
     * @memberOf Checkout
     */
    static setCart(checkout: Checkout, cart: Cart) {
        let item_id = 0;
        let group = 0;
        checkout.cart = cart;
        checkout.idPromo = cart.idPromo;

        // Map older shippings
        const mapshipping = {};
        for (const checkoutItem of checkout.items) {
            mapshipping['s_' + checkoutItem.shippingId] = checkoutItem.shippingOptionSelected;
        }

        // Initalize items
        checkout.items = [];

        // Get Cart Items
        const cartItems = CartHelper.getItems(cart);

        // Each item
        // for (const item of checkout.cart.items) {
        for (const item of cartItems) {
            // Each chipping options
            const shippingAssigned = false;
            for (let shipping_index = 0; shipping_index < item.shippings.length; shipping_index++) {
                const shipping = item.shippings[shipping_index];
                const checkoutItem = CheckoutItemHelper.create();

                checkoutItem.cartItem = item;
                checkoutItem.id = item_id;
                checkoutItem.product_id = item.product_id;
                checkoutItem.price = item.price;
                checkoutItem.priceO = item.priceO;
                // checkoutItem.totalLinea = item.totalLinea;
                // checkoutItem.totalLineaSinDescuento = item.totalLineaSinDescuento;
                checkoutItem.totalLinea = shipping.total;
                checkoutItem.totalLineaSinDescuento = shipping.total;

                // PARTS
                checkoutItem.parts = item.parts;
                checkoutItem.json = item.json;

                if (item.parts) {
                    checkoutItem.quantity = item.quantity;
                    shipping.cantidad = item.quantity;
                } else {
                    checkoutItem.quantity = shipping.cantidad;
                }
                checkoutItem.portesTipoEntrega = item.portesTipoEntrega;

                checkoutItem.shipping = shipping;
                checkoutItem.shippingId = checkoutItem.product_id + '_' + shipping_index;
                checkoutItem.shippingOptions = shipping.shippingOptions;
                checkoutItem.shippingOptionSelected = mapshipping['s_' + checkoutItem.shippingId];

                if (!checkoutItem.shippingOptionSelected || shipping.shippingOptionSelected == '-1') {
                    checkoutItem.shippingOptionSelected = null;
                    for (const shippingOption of shipping.shippingOptions) {
                        if (shippingOption.seleccionado) {
                            checkoutItem.shippingOptionSelected = shippingOption;
                        }
                    }
                }

                checkoutItem.primary = shipping_index === 0;
                checkoutItem.group = group % 2;

                checkout.items.push(checkoutItem);
                item_id++;
            }

            // Group increment
            group++;
        }
    }

    static getTotal(checkout: Checkout, product_id: string): number {
        let total = 0;
        for (const item of checkout.items) {
            if (item.product_id === product_id) {
                total += item.shipping.cantidad;
            }
        }
        return total;
    }

    /**
     * Remove a checkout item by id
     *
     * @param {number} checkoutItemId Position of checkoutItem in array
     * @returns {number}
     *
     * @memberOf Checkout
     */
    static removeItem(checkout: Checkout, checkoutItemId: number): number {
        // console.log('remove');
        const item = checkout.items[checkoutItemId];
        if (item) {
            checkout.items.splice(checkoutItemId, 1);
            return CheckoutHelper.getTotal(checkout, item.product_id);
        }
        return 0;
    }

    /**
     * Remove all items with same product_id
     *
     * @param {string} productId
     *
     * @memberOf Checkout
     */
    static removeProduct(checkout: Checkout, productId: string) {
        const ids = [];
        for (const item of checkout.items) {
            if (item.product_id === productId) {
                ids.push(item.id);
            }
        }

        for (const id of ids) {
            CheckoutHelper.removeItem(checkout, id);
        }
    }

    /**
     * Obtain selected payment
     *
     * @returns
     *
     * @memberOf Checkout
     */
    static getSelectedPayment(checkout: Checkout): Payment {
        if (!checkout.payments) {
            return null;
        }

        if (checkout.selectedPayment) {
            if (checkout.selectedPayment.length === 0) {
                return null;
            }
        }
        // Find payment by descripcion corta
        for (const payment of checkout.payments) {
            if (payment.descripcionCorta === checkout.selectedPayment) {
                return payment;
            }
        }

        return null;
    }

    /**
     * Set payments by reference
     *
     *
     * @memberOf Checkout
     */
    static setPayments(checkout: Checkout, payments: Payment[]) {
        if (checkout) {
            checkout.payments = payments;
        }
    }

    /**
     * Set selected payments by reference
     *
     *
     * @memberOf Checkout
     */
    static setSelectedPayment(checkout: Checkout, selectedPayment: string) {
        checkout.selectedPayment = selectedPayment;
    }

    /**
     * Set agency by reference
     *
     *
     * @memberOf Checkout
     */
    static setAgency(checkout: Checkout, agency: string) {
        if (agency === '1') {
            checkout.agency = true;
        } else {
            checkout.agency = false;
        }
    }

    /**
     * Set annotations
     *
     *
     * @memberOf Checkout
     */
    static setAnnotations(checkout: Checkout, message: string) {
        checkout.annotations = message;
    }

    /**
     * Set promo
     *
     *
     * @memberOf Checkout
     */
    static setPromoCode(cart: Cart, promo: string) {
        cart.promoCode = promo;
    }

    static setEid(checkout: Checkout, eid: string) {
        checkout.eid = eid;
    }

    // /**
    //  * Get promo
    //  *
    //  *
    //  * @memberOf Checkout
    //  */
    // static getPromo(cart: Cart): string {
    //     if (cart.promoCode && cart.promoCode.length > 0) {
    //         console.log(cart.promoCode);
    //         return cart.promoCode;
    //     } else {
    //         return '';
    //     }
    // }

    /**
     * Reset Checkout to initial status
     */
    static clear(checkout: Checkout) {
        checkout.items = [];
        checkout.status = CheckoutStatus.Validated;
        checkout.cart = null;
        checkout.selectedPayment = null;
        checkout.annotations = '';
        checkout.idPromo = '';
        checkout.agency = false;
    }
}
